import React, {FC, useEffect, useMemo, useRef, useState} from 'react';

import Map from '@/components/Map/Map';

import {MapRef} from 'react-map-gl/maplibre';
import type {MapLayerMouseEvent, LngLatBoundsLike} from 'maplibre-gl';

import {HORIZONTAL_DISPLACEMENT_PANEL, INITIAL_VIEWPORT, PRINCIPADO_OVERLAYS_ID, PANEL_WIDTH, PRINCIPADO_OVERLAYS, CETEMAS_OVERLAYS, CATASTRO_OVERLAY, PROJECT_OVERLAYS_ID} from '@/config';

import {parcelLayers, getFeatureCollectionBounds, getParcelBounds, sourcesConstructor, disputesLayer} from '@/domain/services/mapService';
import {Parcel} from '@/domain/entities/Parcel';
import {User} from '@/domain/entities/User';
import {ROLE} from '@/domain/commonTypes';
import GeometryEditor from '@/components/Map/GeometryEditor';
import {FeatureCollection, MultiPolygon, Polygon} from 'geojson';

import CatastroLayer from '@/components/Map/CatastroLayer';
import Overlay from '@/components/Map/Overlay';

import {Mode} from '@/components/Layout';
import {Project} from '@/domain/entities/Project';
import {Dispute} from '@/domain/entities/Dispute';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {SxProps} from '@mui/system';
import {GridExpandMoreIcon} from '@mui/x-data-grid';
import Minimize from '@mui/icons-material/CloseFullscreen';
import Maximize from '@mui/icons-material/OpenInFull';
import Divider from '@mui/material/Divider';
import {useTranslation} from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';

export type OverlayConfig = {
  id: string;
  label: string;
  url: string;
  thumbnail: string;
  legendsUrl: Array<string>;
  legendsWidth: Array<number>;
  sublayers?: Array<string>;
};

export type MainContentProps = {
  project?: Project,
  mapStyle: string,
  selectedOverlays: Array<string>,
  parcels: Array<Parcel>,
  user?: User,
  selectedParcel?: Parcel,
  disputeGeom?: MultiPolygon,
  disputesOverlay?: FeatureCollection<MultiPolygon, Omit<Dispute, 'geom'>>,
  isGeometryVisible: boolean,
  onSelectedParcelUuid: (uuid?: string) => void,
  onDisputeGeomChanged: (geom: Polygon) => void,
  onModeChanged: (mode?: Mode) => void
};

const MapView: FC<MainContentProps> = ({project, mapStyle, selectedOverlays, parcels, selectedParcel, disputeGeom, user, isGeometryVisible, disputesOverlay, onSelectedParcelUuid, onDisputeGeomChanged, onModeChanged}) => {

  const {t} = useTranslation();

  const mapRef = useRef<MapRef| null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const [viewport, setViewport] = useState(INITIAL_VIEWPORT);
  const [showLegend, setShowLegend] = useState(true);

  const selectedOverlayIdsWithoutCadastre: Array<string> = selectedOverlays.filter(overlayId => overlayId !== PRINCIPADO_OVERLAYS_ID.CATASTRO);

  const selectedOverlayWithLegend: Array<string> = selectedOverlays.filter(overlayId => (
    overlayId !== PROJECT_OVERLAYS_ID.DISPUTES &&
    overlayId !== PROJECT_OVERLAYS_ID.PARCEL_FILL)
  );

  const parcelFill = useMemo(() => selectedOverlays.includes(PROJECT_OVERLAYS_ID.PARCEL_FILL), [selectedOverlays]);

  const projectBounds = useMemo(() =>
    getFeatureCollectionBounds(parcels), [parcels]);

  const sources = useMemo(() => 
    sourcesConstructor(selectedOverlays, parcels, disputesOverlay)
  , [parcels, selectedOverlays, disputesOverlay]);

  const layers = useMemo(() => {
    if (user) {
      return [
        ...parcelLayers(parcelFill, user.id, selectedParcel?.uuid),
        ...disputesLayer()
      ];
    }
  }, [user, selectedParcel, selectedOverlays, parcelFill]);

  useEffect(() => {
    if (!mapLoaded) return;
    const bounds = selectedParcel ? getParcelBounds(selectedParcel) : projectBounds;
    if (bounds) zoomToBounds(bounds);
  }, [selectedParcel, projectBounds, mapLoaded]);

  const zoomToBounds = (bounds: LngLatBoundsLike) => {
    mapRef.current?.fitBounds(
      bounds,
      {
        padding: {
          top: 40,
          bottom: 40,
          right: 40,
          left: selectedParcel ? PANEL_WIDTH.parcel+HORIZONTAL_DISPLACEMENT_PANEL+40 : 40
        }
      }
    );
  };

  const handleMapClick = (e: MapLayerMouseEvent) => {
    const feature = e.features && e.features[0];
    if(!isGeometryVisible) {
      if (feature){
        onSelectedParcelUuid(feature.properties?.uuid);
        onModeChanged(Mode.PARCEL);
      } else {
        onSelectedParcelUuid(undefined);
        onModeChanged(undefined);
      }
    }
  };
  
  return <Map
    ref={mapRef}
    mapStyle={mapStyle}
    viewport={viewport}
    onViewportChange={setViewport}
    styleDiffing={true}
    sources={sources}
    layers={layers}
    onLoad={() => setMapLoaded(true)}
    onClick={handleMapClick}
    interactiveLayerIds={
      [ROLE.TECHNICIAN, ROLE.ADMIN].includes(`${user?.role}`)
        ? ['user-fill', 'others-fill']
        : ['user-fill']
    }
  >
    {selectedParcel && isGeometryVisible && <GeometryEditor geometry={disputeGeom || selectedParcel.geom} onGeomChanged={onDisputeGeomChanged}/>}
    {selectedOverlays.includes(PRINCIPADO_OVERLAYS_ID.CATASTRO) && <CatastroLayer beforeId={'others-fill'}/>}
    {
      selectedOverlayIdsWithoutCadastre
        .map(overlayId => <Overlay
          project={project}
          key={overlayId}
          overlayId={overlayId}
          beforeId={'others-fill'}
          transparent={true}/>
        )
    }
    {
      selectedOverlayWithLegend.length &&
      <Box sx={{...styles.legendsGroup, width: showLegend ? 332 : 38}}>
        <Tooltip title={showLegend ? t('legend.hide') : t('legend.show')}>
          <Button sx={styles.minmaximizeButton} onClick={() => setShowLegend(!showLegend)}>
            {showLegend ? <Minimize sx={styles.icon}/> : <Maximize sx={styles.icon}/> }
          </Button>
        </Tooltip>
        
        {
          showLegend && project && getOverlaysLegend(selectedOverlayWithLegend, project).map(({id, label, legendsUrl, legendsWidth, sublayers}) => <Accordion key={`legend_${id}`}>
            <AccordionSummary key={id} expandIcon={<GridExpandMoreIcon/>}>
              <Typography variant="overline" sx={styles.legendLabel}>{label}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={styles.accordionDetails}>
              {
                legendsUrl.map((url, key) => <>
                  {sublayers && <Typography variant="body2" sx={styles.sublayers}>{sublayers[key].replace(/_/g, ' ')}</Typography>}
                  {sublayers && <Divider sx={styles.divider}/>}
                  <Box key={key} component="img" src={url} sx={{width: legendsWidth[key]}}/>
                </>)
              }
            </AccordionDetails>
          </Accordion>)
        }
      </Box>
    }
  </Map>;
};

export default MapView;

const getOverlaysLegend = (selectedOverlayWithLegend: Array<string>, project: Project):
  Array<{ 
  id: string; 
  label: string; 
  legendsUrl: Array<string>; 
  legendsWidth: Array<number>;
  sublayers?: Array<string>
}> => {

  const allOverlays: Array<OverlayConfig> = [
    ...PRINCIPADO_OVERLAYS,
    ...CETEMAS_OVERLAYS(project.zone.toLowerCase()),
    ...CATASTRO_OVERLAY
  ];

  return selectedOverlayWithLegend
    .map(id => {
      const overlay = allOverlays.find(overlay => overlay.id === id);

      if (!overlay) {
        return {
          id,
          label: 'Unknown',
          legendsUrl: [],
          legendsWidth: [],
          sublayers: undefined
        };
      }

      return {
        id: overlay.id,
        label: overlay.label,
        legendsUrl: overlay.legendsUrl ?? [],
        legendsWidth: overlay.legendsWidth ?? [],
        sublayers: overlay.sublayers ?? undefined
      };
    });
};

const styles: Record<string, SxProps> = {
  legendsGroup: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    right: 10,
    top: 10,
    m: 1,
    p: 0,
    width: 332,
    maxHeight: '95vh',
    overflowY: 'auto'
  },
  legendLabel: {
    lineHeight: 1.5,
    fontWeight: 700
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    maxHeight: '80vh',
    maxWidth: '100%'
  },
  minmaximizeButton: {
    alignSelf: 'end',
    mb: 1,
    bgcolor: 'common.white',
    minWidth: '32px'
  },
  icon: {
    color: 'grey.700'
  },
  sublayers: {
    fontWeight: 700,
    mt: 2
  },
  divider: {
    mb: 1
  }
};
